import { useQuery } from "@tanstack/react-query";

import { opsAdmin } from "@/api/opsAdmin";
import { Badge, Card, CardBody, CardHeader } from "@/components/ui";

/**
 * System status — live health of the platform: DB, Redis, background daemons,
 * and LP connectivity. Read-only operator view of the public /status check.
 */
export function AdminStatusPage() {
  const status = useQuery({
    queryKey: ["system-status"],
    queryFn: () => opsAdmin.status(),
    refetchInterval: 10_000,
  });

  const s = status.data;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">System status</h1>
        {s && (
          <Badge tone={s.status === "ok" ? "good" : "bad"}>{s.status.toUpperCase()}</Badge>
        )}
      </div>

      {status.isLoading ? (
        <div className="p-3 text-sm text-term-mute">Loading…</div>
      ) : !s ? (
        <Card>
          <CardBody>
            <div className="text-sm text-bad">Status check failed to load.</div>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <HealthCard label="Service" up={s.service_up} />
            <HealthCard label="Database" up={s.db} />
            <HealthCard label="Redis" up={s.redis} />
          </div>

          <Card>
            <CardHeader>
              <span className="text-sm font-medium">Background daemons</span>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Object.entries(s.daemons).map(([name, up]) => (
                  <div key={name} className="flex items-center justify-between rounded-sm bg-term-elev px-2 py-1.5">
                    <span className="text-2xs text-term-dim">{name}</span>
                    <Badge tone={up ? "good" : "neutral"}>{up ? "up" : "off"}</Badge>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <span className="text-sm font-medium">Liquidity providers</span>
            </CardHeader>
            <CardBody>
              {Object.keys(s.lp_status).length === 0 ? (
                <div className="text-2xs text-term-mute">No LP sessions reporting.</div>
              ) : (
                <div className="space-y-1">
                  {Object.entries(s.lp_status).map(([name, st]) => (
                    <div key={name} className="flex items-center justify-between text-2xs">
                      <span className="font-mono text-term-dim">{name}</span>
                      <span className="text-term-fg">{String(st)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <p className="text-2xs text-term-mute">
            Checked {new Date(s.checked_at).toLocaleString()} · auto-refreshes every 10s
          </p>
        </>
      )}
    </div>
  );
}

function HealthCard({ label, up }: { label: string; up: boolean }) {
  return (
    <Card>
      <CardBody className="flex items-center justify-between">
        <span className="text-sm text-term-fg">{label}</span>
        <Badge tone={up ? "good" : "bad"}>{up ? "Healthy" : "Down"}</Badge>
      </CardBody>
    </Card>
  );
}
